import React from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import normalizeUrl from 'normalize-url'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { BtnV2, Link, l, SectionMessage, TextInputV2 } from '@manychat/manyui'

import { useAppSelector } from 'reduxTyped'
import { useKnowledgeEditorContext } from 'apps/aiAutomation/components/KnowledgeEditor/lib/context'
import { AiAutomationKnowledgeSelectors } from 'apps/aiAutomation/store/knowledge'
import { URL_RULE_WITH_OPTIONAL_PROTOCOL } from 'constants/RegExs'
import { KnowledgeErrorCode } from 'shared/api/requests/aiBusinessKnowledge/schemas'

import { getErrorContent } from '../../lib/getErrorContent'

interface LinkFormProps {
  url?: string
  onSubmit: (url: string) => void
  error: KnowledgeErrorCode | null
}

export const LinkForm = ({ url, error, onSubmit }: LinkFormProps) => {
  const { onClose } = useKnowledgeEditorContext()
  const links = useAppSelector(AiAutomationKnowledgeSelectors.getUsedLinks)

  const { handleSubmit, register, formState } = useForm({
    defaultValues: {
      url: url || '',
    },
    resolver: zodResolver(
      z.object({
        url: z
          .string()
          .min(1, l.translate('Add a link to continue'))
          .max(2000, l.translate('Link is too long'))
          .regex(URL_RULE_WITH_OPTIONAL_PROTOCOL, l.translate('Invalid link'))
          .refine(
            (value) => {
              return (
                url ||
                links.filter((link) => normalizeUrl(link) === normalizeUrl(value)).length === 0
              )
            },
            {
              message: l.translate('This link is already added.'),
            },
          ),
      }),
    ),
  })

  const onNext = ({ url }: { url: string }) => {
    onSubmit(url)
  }

  const { ref: urlRef, ...urlProps } = register('url')

  const errorContent = getErrorContent(error)

  return (
    <>
      {error && (
        <SectionMessage
          className="m-b-lg"
          variant="error"
          title={errorContent?.title}
          description={
            <p>
              {errorContent?.description}
              <Link
                className="d-inline"
                as="a"
                contentColor="subtle"
                target="_blank"
                href="https://help.manychat.com/hc/en-us/articles/25626595060124-Manychat-AI-Knowledge"
                underline
                variant="body"
              >
                {l.translate(
                  '<l10n context="Try a different link or learn how to fix this"/>learn how to fix this',
                )}
              </Link>
            </p>
          }
        />
      )}
      <TextInputV2
        inputRef={urlRef}
        {...urlProps}
        label={l.translate('<l10n context="Noun"/>Link')}
        errorText={formState.errors.url?.message}
        helpText={l.translate(
          'AI reads text only from this page. Images, videos and other pages are excluded.',
        )}
        invalid={Boolean(formState.errors.url?.message)}
        placeholder={l.translate('e.g. https://manychat.com')}
        data-test-id="ai-automation/knowledge-editor-link-input"
      />

      <div className="d-flex justify-end m-t-sm">
        <BtnV2
          variant="primary"
          size="small"
          onClick={handleSubmit(onNext)}
          data-test-id="ai-automation/knowledge-preview-link-button"
          label={l.translate('Scan now')}
        />
      </div>

      <div className="d-flex gap-sm m-t-auto">
        <BtnV2
          ariaLabel={l.translate('Close')}
          label={l.translate('Close')}
          size="default"
          type="button"
          variant="default"
          onClick={onClose}
        />
      </div>
    </>
  )
}
